import React, { useState, useEffect, useMemo } from "react";
import styled from "styled-components";
import { Player } from "../../types";
import { dictionaries } from "../../utils/dictionary";
import {
  SectionCard,
  InstructionCard,
  SuccessCard,
  ErrorCard,
  PhaseButton,
  PlayerAvatar,
  MetaInfo,
  MetaHighlight,
} from "../../styles/SharedStyles";
import { theme } from "../../styles/theme";

interface VotingProps {
  language: number;
  players: Player[];
  playersLeaved: number[];
  nominations: Record<string, string>;
  nightCount: number;
  firstTalk: number;
  getOrderedPlayersFromFirstTalk: (
    firstTalkIndex: number,
    playersLeaved: number[]
  ) => number[];
  onVotingComplete: (results: VotingResult) => void;
  onCancel: () => void;
}

interface VotingResult {
  eliminatedPlayers: number[];
  voteStats: number[];
  maxVotes: number;
}

type VotingPhase = "voting" | "shootout" | "tie-decision";
type TieDecision = "execute-all" | "save-all" | "cancel";

const VotingCard = styled(SectionCard)`
  background: linear-gradient(
    145deg,
    rgba(22, 14, 42, 0.96),
    rgba(12, 8, 28, 0.96)
  );
  border: 1px solid rgba(124, 93, 255, 0.35);
  box-shadow: 0 20px 60px rgba(10, 6, 25, 0.45);
`;

const VotingHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const VotingTitle = styled.h5`
  margin: 0;
  font-size: 1.1rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #dcd4ff;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const VotingTitleText = styled.span`
  @media (max-width: ${theme.breakpoints.mobile}) {
    flex-basis: 100%;
  }
`;

const CloseButton = styled.button.attrs({ type: "button" })`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.16);
  color: ${theme.colors.text.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all ${theme.transitions.normal};
  font-size: 1.25rem;

  &:hover {
    background: rgba(255, 255, 255, 0.18);
    transform: rotate(90deg);
  }
`;

const CandidateBadge = styled.span<{ $active?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 32px;
  height: 32px;
  padding: 0 0.5rem;
  border-radius: 12px;
  font-weight: 700;
  font-size: 0.9rem;
  background: ${({ $active }) =>
    $active
      ? "linear-gradient(135deg, rgba(255, 96, 122, 0.95), rgba(214, 38, 78, 0.95))"
      : "rgba(255, 255, 255, 0.12)"};
  color: "#ffffff";
  border: 1px solid
    ${({ $active }) =>
      $active ? "rgba(255, 96, 122, 0.5)" : "rgba(255, 255, 255, 0.18)"};
`;

const CandidateSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  margin-bottom: 1.5rem;

  @media (min-width: ${theme.breakpoints.mobile}) {
    flex-direction: row;
    align-items: center;
  }
`;

const CandidateInfo = styled.div`
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem;
  border-radius: ${theme.borderRadius.lg};
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.12);
`;

const CandidateAvatar = styled(PlayerAvatar)`
  width: 56px;
  height: 56px;
  border-radius: ${theme.borderRadius.lg};
  border: 2px solid rgba(255, 96, 122, 0.5);
`;

const CandidateNumber = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 12px;
  font-weight: 700;
  font-size: 1.1rem;
  background: linear-gradient(
    135deg,
    rgba(255, 96, 122, 0.95),
    rgba(214, 38, 78, 0.95)
  );
  color: #ffffff;
  border: 1px solid rgba(255, 96, 122, 0.5);
`;

const CandidateName = styled.strong`
  font-size: 1rem;
  text-align: center;
`;

const VotingSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const VoteButtonsGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  justify-content: center;
`;

const VoteButton = styled.button.attrs({ type: "button" })<{
  $active?: boolean;
}>`
  min-width: 48px;
  height: 48px;
  padding: 0 0.75rem;
  border-radius: 14px;
  font-weight: 700;
  font-size: 1rem;
  border: 1px solid
    ${({ $active }) =>
      $active ? "rgba(68, 255, 210, 0.65)" : "rgba(255, 255, 255, 0.18)"};
  background: ${({ $active }) =>
    $active
      ? "linear-gradient(135deg, rgba(68, 255, 210, 0.35), rgba(124, 93, 255, 0.55))"
      : "rgba(255, 255, 255, 0.05)"};
  color: ${theme.colors.text.primary};
  cursor: pointer;
  transition: transform 0.15s ease-out, background 0.15s ease-out;
  -webkit-tap-highlight-color: transparent;

  @media (hover: hover) and (pointer: fine) {
    &:hover:not(:disabled) {
      transform: translateY(-1px);
      background: linear-gradient(
        135deg,
        rgba(68, 255, 210, 0.45),
        rgba(124, 93, 255, 0.65)
      );
    }
  }

  &:active:not(:disabled) {
    transform: scale(0.95);
  }

  &:disabled {
    opacity: 0.35;
    cursor: not-allowed;
    border-color: rgba(255, 255, 255, 0.12);
  }
`;

const ResultsCard = styled.div`
  padding: 0.9rem 1.1rem;
  border-radius: ${theme.borderRadius.lg};
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.12);
  margin-bottom: 1rem;
`;

const ResultsGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.5rem;
`;

const ResultBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.35rem 0.75rem;
  border-radius: ${theme.borderRadius.round};
  background: rgba(124, 93, 255, 0.25);
  border: 1px solid rgba(124, 93, 255, 0.45);
  font-weight: 600;
  font-size: 0.85rem;
`;

const ActionButtons = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  justify-content: center;
  margin-top: 1.5rem;
`;

const HistoryCard = styled.div`
  padding: 0.75rem 1rem;
  border-radius: ${theme.borderRadius.md};
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.12);
  margin-bottom: 1rem;
  font-size: 0.85rem;
`;

const HistoryItem = styled.div`
  padding: 0.25rem 0;
  opacity: 0.85;
`;

const Voting: React.FC<VotingProps> = ({
  language,
  players,
  playersLeaved,
  nominations,
  nightCount,
  firstTalk,
  getOrderedPlayersFromFirstTalk,
  onVotingComplete,
  onCancel,
}) => {
  const [currentCandidateIndex, setCurrentCandidateIndex] = useState(0);
  const [voteResults, setVoteResults] = useState<number[]>([]);
  const [totalVotesUsed, setTotalVotesUsed] = useState(0);
  const [phase, setPhase] = useState<VotingPhase>("voting");
  const [shootoutRound, setShootoutRound] = useState(0);
  const [tiedCandidates, setTiedCandidates] = useState<number[]>([]);
  const [votingHistory, setVotingHistory] = useState<
    Array<{ round: number; results: number[]; tiedPlayers: number[] }>
  >([]);

  const dict = dictionaries[language].ph;

  const baseCandidates = useMemo(() => {
    const orderedPlayers = getOrderedPlayersFromFirstTalk(
      firstTalk,
      playersLeaved
    );
    const seenCandidates = new Set<number>();
    const candidatesInOrder: number[] = [];

    // Iterate through nominators in speaking order
    orderedPlayers.forEach((nominatorNum) => {
      const nominatorIndex = nominatorNum - 1;
      const nomination = nominations[nominatorIndex.toString()];

      if (nomination) {
        const candidateNum = parseInt(nomination);
        if (
          !seenCandidates.has(candidateNum) &&
          !playersLeaved.includes(candidateNum)
        ) {
          seenCandidates.add(candidateNum);
          candidatesInOrder.push(candidateNum);
        }
      }
    });

    return candidatesInOrder.map((playerNum) => ({
      playerNumber: playerNum,
      playerName: players[playerNum - 1]?.nickname || `Player ${playerNum}`,
      votes: 0,
    }));
  }, [
    nominations,
    players,
    playersLeaved,
    firstTalk,
    getOrderedPlayersFromFirstTalk,
  ]);

  const candidates =
    phase === "shootout"
      ? tiedCandidates.map((playerNum) => ({
          playerNumber: playerNum,
          playerName: players[playerNum - 1]?.nickname || `Player ${playerNum}`,
          votes: 0,
        }))
      : baseCandidates;

  const alivePlayers = players.filter(
    (_, index) => !playersLeaved.includes(index + 1)
  );
  const totalVotesAvailable = alivePlayers.length - totalVotesUsed;

  useEffect(() => {
    if (voteResults.length === 0 && candidates.length > 0) {
      setVoteResults(new Array(candidates.length).fill(0));
    }
  }, [candidates.length, voteResults.length]);

  const handleVote = (votes: number) => {
    if (currentCandidateIndex < candidates.length) {
      const newResults = [...voteResults];

      if (currentCandidateIndex === candidates.length - 1) {
        newResults[currentCandidateIndex] = totalVotesAvailable;
        setTotalVotesUsed(alivePlayers.length);
      } else {
        newResults[currentCandidateIndex] = votes;
        setTotalVotesUsed((prev) => prev + votes);
      }

      setVoteResults(newResults);

      const newTotalUsed =
        currentCandidateIndex === candidates.length - 1
          ? alivePlayers.length
          : totalVotesUsed + votes;

      if (
        newTotalUsed >= alivePlayers.length ||
        currentCandidateIndex === candidates.length - 1
      ) {
        processVotingResults(newResults);
      } else {
        setCurrentCandidateIndex((prev) => prev + 1);
      }
    }
  };

  const processVotingResults = (results: number[]) => {
    const maxVotes = Math.max(...results);
    const tiedPlayersThisRound = candidates
      .filter((_, index) => results[index] === maxVotes)
      .map((candidate) => candidate.playerNumber);

    const newHistory = [
      ...votingHistory,
      {
        round: shootoutRound + 1,
        results: [...results],
        tiedPlayers: tiedPlayersThisRound,
      },
    ];
    setVotingHistory(newHistory);

    if (tiedPlayersThisRound.length > 1) {
      const previousTiedCount = tiedCandidates.length;
      const currentTiedCount = tiedPlayersThisRound.length;

      if (
        phase === "voting" ||
        (phase === "shootout" && currentTiedCount !== previousTiedCount)
      ) {
        startShootout(results, maxVotes, tiedPlayersThisRound);
      } else {
        setPhase("tie-decision");
      }
    } else {
      const eliminatedPlayer = tiedPlayersThisRound[0];
      completeVoting([eliminatedPlayer], results, maxVotes);
    }
  };

  const startShootout = (
    results: number[],
    maxVotes: number,
    tiedPlayersThisRound: number[]
  ) => {
    setTiedCandidates(tiedPlayersThisRound);
    setPhase("shootout");
    setShootoutRound((prev) => prev + 1);
    setCurrentCandidateIndex(0);
    setVoteResults(new Array(tiedPlayersThisRound.length).fill(0));
    setTotalVotesUsed(0);
  };

  const handleTieDecision = (decision: TieDecision) => {
    if (decision === "execute-all") {
      const alivePlayersCount = alivePlayers.length;
      const maxExecutions = Math.floor(alivePlayersCount / 2);

      if (tiedCandidates.length > maxExecutions) {
        alert(
          dict.cannot_execute_more
            .replace('{0}', String(maxExecutions))
            .replace('{1}', String(alivePlayersCount))
        );
        return;
      }

      if (alivePlayersCount === 9 && tiedCandidates.length === 3) {
        alert(
          dict.cannot_execute_three_way_tie
        );
        return;
      }

      completeVoting(tiedCandidates, voteResults, Math.max(...voteResults));
    } else if (decision === "save-all") {
      completeVoting([], voteResults, 0);
    } else {
      onCancel();
    }
  };

  const completeVoting = (
    eliminatedPlayers: number[],
    results: number[],
    maxVotes: number
  ) => {
    const votingResult: VotingResult = {
      eliminatedPlayers,
      voteStats: results,
      maxVotes,
    };

    onVotingComplete(votingResult);
  };

  const handleSkipVoting = () => {
    const votingResult: VotingResult = {
      eliminatedPlayers: [],
      voteStats: [],
      maxVotes: 0,
    };

    onVotingComplete(votingResult);
  };

  if (candidates.length === 0) {
    return (
      <InstructionCard>
        <VotingHeader>
          <VotingTitle>{dict.no_nominations_title}</VotingTitle>
          <CloseButton onClick={onCancel}>×</CloseButton>
        </VotingHeader>
        <p style={{ marginBottom: "1rem" }}>{dict.no_nominations_desc}</p>
        <ActionButtons>
          <PhaseButton $variant="primary" onClick={handleSkipVoting}>
            {dict.gngt}
          </PhaseButton>
          <PhaseButton $variant="secondary" onClick={onCancel}>
            {dict.back}
          </PhaseButton>
        </ActionButtons>
      </InstructionCard>
    );
  }

  if (candidates.length === 1 && nightCount === 0) {
    return (
      <SuccessCard>
        <VotingHeader>
          <VotingTitle>{dict.novoting}</VotingTitle>
          <CloseButton onClick={onCancel}>×</CloseButton>
        </VotingHeader>
        <p style={{ marginBottom: "1rem" }}>
          {dict.single_nomination.replace(
            '{0}',
            candidates[0].playerName
          )}
        </p>
        <ActionButtons>
          <PhaseButton $variant="primary" onClick={handleSkipVoting}>
            {dict.gngt}
          </PhaseButton>
          <PhaseButton $variant="secondary" onClick={onCancel}>
            {dict.cncl}
          </PhaseButton>
        </ActionButtons>
      </SuccessCard>
    );
  }

  if (candidates.length === 1 && nightCount > 0) {
    return (
      <ErrorCard>
        <VotingHeader>
          <VotingTitle>
            {dict.bye1player} {candidates[0].playerNumber} {dict.bye1player2}
          </VotingTitle>
          <CloseButton onClick={onCancel}>×</CloseButton>
        </VotingHeader>
        <p style={{ marginBottom: "1rem" }}>
          {dict.player_will_be_eliminated.replace(
            '{0}',
            candidates[0].playerName
          )}
        </p>
        <ActionButtons>
          <PhaseButton
            $variant="danger"
            onClick={() => completeVoting([candidates[0].playerNumber], [1], 1)}
          >
            {dict.exec}
          </PhaseButton>
          <PhaseButton $variant="secondary" onClick={onCancel}>
            {dict.cncl}
          </PhaseButton>
        </ActionButtons>
      </ErrorCard>
    );
  }

  if (phase === "tie-decision") {
    const aliveCount = alivePlayers.length;
    const maxExecutions = Math.floor(aliveCount / 2);
    const wouldExceedLimit = tiedCandidates.length > maxExecutions;
    const isSpecialThreeWayTie = aliveCount === 9 && tiedCandidates.length === 3;

    return (
      <VotingCard>
        <VotingHeader>
          <VotingTitle>⚖️ {dict.citydec}</VotingTitle>
          <CloseButton onClick={onCancel}>×</CloseButton>
        </VotingHeader>

        <MetaInfo style={{ marginBottom: "1rem", textAlign: "left" }}>
          {dict.tie_repeat_info}
        </MetaInfo>

        <MetaInfo style={{ marginBottom: "1.5rem", textAlign: "left" }}>
          <MetaHighlight>{dict.tied_players}</MetaHighlight>{" "}
          {tiedCandidates.join(", ")} 
        </MetaInfo>

        {isSpecialThreeWayTie && (
          <ErrorCard style={{ marginBottom: "1rem" }}>
            <strong>⚠️ {dict.three_way_tie_rule}</strong>
            <div style={{ marginTop: "0.5rem" }}>
              {dict.cannot_execute_three_way_tie}
            </div>
          </ErrorCard>
        )}

        {wouldExceedLimit && !isSpecialThreeWayTie && (
          <ErrorCard style={{ marginBottom: "1rem" }}>
            <strong>{dict.rule_50_percent}</strong> {dict.cannot_execute_more.replace('{0}', String(maxExecutions)).replace('{1}', String(aliveCount))}
            <div style={{ marginTop: "0.5rem" }}>
              <strong>
                ⚠️ {dict.tie_exceeds_limit_warning.replace(
                  '{0}',
                  String(tiedCandidates.length)
                )}
              </strong>
            </div>
          </ErrorCard>
        )}

        <InstructionCard style={{ marginBottom: "1.5rem" }}>
          <strong>{dict.select_action}</strong>
        </InstructionCard>

        <ActionButtons>
            <PhaseButton
              $variant={(wouldExceedLimit || isSpecialThreeWayTie) ? "outline" : "danger"}
              onClick={() => handleTieDecision("execute-all")}
              disabled={wouldExceedLimit || isSpecialThreeWayTie}
              title={
                isSpecialThreeWayTie
                  ? (dict.cannot_execute_three_way_tie)
                  : wouldExceedLimit
                  ? dict.tie_execute_limit_tooltip
                      .replace('{0}', String(tiedCandidates.length))
                      .replace('{1}', String(maxExecutions))
                  : dict.tie_execute_tooltip.replace(
                      '{0}',
                      String(tiedCandidates.length)
                    )
              }
            >
              {dict.execall}
          </PhaseButton>
          <PhaseButton
            $variant="primary"
            onClick={() => handleTieDecision("save-all")}
          >
            {dict.gngt}
          </PhaseButton>
          <PhaseButton
            $variant="secondary"
            onClick={() => handleTieDecision("cancel")}
          >
            {dict.cncl}
          </PhaseButton>
        </ActionButtons>
      </VotingCard>
    );
  }

  const currentCandidate = candidates[currentCandidateIndex];
  const isLastCandidate = currentCandidateIndex === candidates.length - 1;
  const votingPhase =
    phase === "shootout"
      ? dict.shootout_round.replace('{0}', String(shootoutRound))
      : dict.voting;

  return (
    <VotingCard>
      <VotingHeader>
        <VotingTitle>
          <VotingTitleText>{votingPhase}:</VotingTitleText>
          {candidates.map((c, i) => (
            <CandidateBadge
              key={c.playerNumber}
              $active={i === currentCandidateIndex}
            >
              {c.playerNumber}
            </CandidateBadge>
          ))}
        </VotingTitle>
        <CloseButton onClick={onCancel}>×</CloseButton>
      </VotingHeader>

      <CandidateSection>
        <VotingSection>
          {isLastCandidate ? (
            <InstructionCard>
              <strong>
                {dict.candidate_receives_remaining
                  .replace('{0}', String(currentCandidate.playerNumber))
                  .replace('{1}', String(totalVotesAvailable))}
              </strong>
            </InstructionCard>
          ) : (
            <VoteButtonsGrid>
              {Array.from(
                { length: Math.min(totalVotesAvailable + 1, 11) },
                (_, i) => (
                  <VoteButton
                    key={i}
                    onClick={() => handleVote(i)}
                    disabled={i > totalVotesAvailable}
                  >
                    {i}
                  </VoteButton>
                )
              )}
            </VoteButtonsGrid>
          )}

          <MetaInfo>
            {dict.candidate_progress
              .replace('{0}', String(currentCandidateIndex + 1))
              .replace('{1}', String(candidates.length))
              .replace('{2}', String(totalVotesAvailable))}
          </MetaInfo>
        </VotingSection>
      </CandidateSection>

      {voteResults.some((v) => v > 0) && (
        <ResultsCard>
          <strong>{dict.current_results}</strong>
          <ResultsGrid>
            {candidates
              .slice(
                0,
                isLastCandidate
                  ? currentCandidateIndex + 1
                  : currentCandidateIndex
              )
              .map((candidate, index) => (
                <ResultBadge key={candidate.playerNumber}>
                  {candidate.playerNumber}:{" "}
                  {index === currentCandidateIndex && isLastCandidate
                    ? totalVotesAvailable
                    : voteResults[index]}
                </ResultBadge>
              ))}
          </ResultsGrid>
        </ResultsCard>
      )}

      <ActionButtons>
        {isLastCandidate ? (
          <PhaseButton $variant="primary" onClick={() => handleVote(0)}>
            {dict.finish_voting}
          </PhaseButton>
        ) : (
          <PhaseButton
            $variant="primary"
            onClick={() => handleVote(totalVotesAvailable)}
          >
            {dict.all_remaining_votes}
          </PhaseButton>
        )}
        <PhaseButton $variant="secondary" onClick={onCancel}>
          {dict.cncl}
        </PhaseButton>
      </ActionButtons>
    </VotingCard>
  );
};

export default Voting;
